import { useCallback, useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot, orderBy, query, setDoc, updateDoc } from "firebase/firestore";
import dayjs from "dayjs";
import { firestore } from "../../../configs/FirebaseConfig";
import { COMPANY_COUNTRY_CODE } from "../../../constants/CompanyConstants";
import { FIRESTORE_CRATE_STOCKS_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../constants/FirestoreConstant";

const STATUS_AVAILABLE = "available";
const STATUS_IN_USE = "in_use";

export const useCrateStocks = () => {
  const [crateStocks, setCrateStocks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const crateStocksCollectionRef = useMemo(() => {
    return collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_CRATE_STOCKS_TABLE);
  }, []);

  const getCrateDocRef = useCallback((crateDocId) => {
    return doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_CRATE_STOCKS_TABLE, crateDocId);
  }, []);

  const getCrateEventsCollectionRef = useCallback((crateDocId) => {
    return collection(
      firestore,
      FIRESTORE_REGIONS_TABLE,
      COMPANY_COUNTRY_CODE,
      FIRESTORE_CRATE_STOCKS_TABLE,
      crateDocId,
      "events",
    );
  }, []);

  const upsertCrateStock = useCallback(
    async (crateDocId, data, updatedBy = null) => {
      const crateRef = getCrateDocRef(crateDocId);
      const nowUnix = dayjs().unix();
      await setDoc(
        crateRef,
        {
          ...data,
          updatedAt: nowUnix,
          updatedBy: updatedBy || data?.updatedBy || null,
          createdAt: data?.createdAt ?? nowUnix,
        },
        { merge: true },
      );
    },
    [getCrateDocRef],
  );

  const updateCrateStock = useCallback(
    async (crateDocId, data, updatedBy = null) => {
      const crateRef = getCrateDocRef(crateDocId);
      const nowUnix = dayjs().unix();
      await updateDoc(crateRef, {
        ...data,
        updatedAt: nowUnix,
        updatedBy: updatedBy || null,
      });
    },
    [getCrateDocRef],
  );

  const addCrateEvent = useCallback(
    async (crateDocId, event) => {
      const eventsRef = getCrateEventsCollectionRef(crateDocId);
      const newEventRef = doc(eventsRef);
      const nowUnix = dayjs().unix();
      await setDoc(newEventRef, {
        ...event,
        createdAt: nowUnix,
      });

      return newEventRef.id;
    },
    [getCrateEventsCollectionRef],
  );

  const setCrateAvailable = useCallback(
    async (crateDocId, updatedBy = null) => {
      await updateCrateStock(
        crateDocId,
        {
          status: STATUS_AVAILABLE,
          items: [],
          itemsCount: 0,
        },
        updatedBy,
      );
      await addCrateEvent(crateDocId, {
        type: "SET_AVAILABLE",
      });
    },
    [addCrateEvent, updateCrateStock],
  );

  const setCrateInUse = useCallback(
    async (crateDocId, payload = {}, updatedBy = null) => {
      const itemsCount = typeof payload.itemsCount === "number" ? payload.itemsCount : undefined;

      await updateCrateStock(
        crateDocId,
        {
          status: STATUS_IN_USE,
          ...(payload.items ? { items: payload.items } : {}),
          ...(itemsCount !== undefined ? { itemsCount } : {}),
        },
        updatedBy,
      );
      await addCrateEvent(crateDocId, {
        type: "SET_IN_USE",
        ...(payload.items ? { items: payload.items } : {}),
        ...(itemsCount !== undefined ? { itemsCount } : {}),
      });
    },
    [addCrateEvent, updateCrateStock],
  );

  useEffect(() => {
    setIsLoading(true);

    const q = query(crateStocksCollectionRef, orderBy("crateId", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const crates = [];
        snapshot.forEach((doc) => {
          crates.push({
            id: doc.id,
            ...doc.data(),
          });
        });
        setCrateStocks(crates);
        setIsLoading(false);
        setError(null);
      },
      (err) => {
        console.error("Error fetching crate stocks:", err);
        setError(err.message);
        setIsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [crateStocksCollectionRef]);

  return {
    crateStocks,
    isLoading,
    error,
    upsertCrateStock,
    updateCrateStock,
    addCrateEvent,
    setCrateAvailable,
    setCrateInUse,
  };
};
